import { useQuery } from '@apollo/client';
import axios from 'axios';
import { graphql, useStaticQuery } from 'gatsby';
import { find, head, propEq } from 'ramda';
import React, { FC, useEffect, useState } from 'react';
import { useAsync, useDeepCompareEffect } from 'react-use';
import styled from 'styled-components';

import FlexColumn from '@ui/atoms/FlexColumn';
import LoadingWrapper from '@ui/atoms/LoadingWrapper';
import Skeleton from '@ui/atoms/Skeleton';

import fullWidth from '@ui/styled/FullWidth';

import { keysToCamel } from '@utils/butter';
import { ApiEndpoints } from '@utils/endpoints';
import { doesNotExist, exists } from '@utils/general';

import { getAuthToken, useAuth } from '@providers/AuthProvider';

import { IPageProps } from '@folx-types/flow-steps';
import {
	IAccountInfoQueryData,
	IDefaultMedicationDeliveryMethodResponse,
} from '@folx-types/member';
import { ISubscriptionResponse } from '@folx-types/pricing';

import currentMemberWithSubscriptionsQuery from '@graphql/queries/currentMemberWithSubscriptions.gql';
import defaultMedicationDeliveryMethod from '@graphql/queries/defaultMedicationDeliveryMethod.query.gql';

import MedicationDeliveryPostSubmitStep, {
	IMedicationDeliveryPostSubmitStepPageContent,
} from './MedicationDeliveryPostSubmitStep.component';

interface IMedicationDeliveryPostSubmitStepPageProps extends IPageProps {}

const FullWidthColumn = fullWidth(FlexColumn);

const SkeletonLink = styled(Skeleton)`
	width: 20rem;
`;

const MedicationDeliveryPostSubmitStepPage: FC<IMedicationDeliveryPostSubmitStepPageProps> = ({
	path,
	...props
}) => {
	const { pageContent } = useStaticQuery(graphql`
		{
			pageContent: butterPage(slug: { eq: "medication-delivery-post-submit-step" }) {
				heading
				capsule_pharmacy_text
				folx_pharmacy_text
				capsule_info_link_text
				capsule_info_link_url
				return_button_text
			}
		}
	`);

	const [loading, setLoading] = useState(true);

	const { loggedIn } = useAuth();

	const {
		loading: subscriptionLoading,
		error: subscriptionError,
		data: subscriptionData,
	} = useQuery<IAccountInfoQueryData>(currentMemberWithSubscriptionsQuery, {
		skip: !loggedIn,
	});

	const {
		loading: defaultDeliveryMethodLoading,
		error: defaultDeliveryMethodError,
		data: defaultDeliveryMethodData,
	} = useQuery<IDefaultMedicationDeliveryMethodResponse>(defaultMedicationDeliveryMethod, {
		skip: !loggedIn,
	});

	const {
		loading: deliveryMethodLoading,
		error: deliveryMethodError,
		value: deliveryMethod,
	} = useAsync(async () => {
		const authorizationToken = await getAuthToken();
		const deliveryMethodData = await axios.get(ApiEndpoints.deliveryMethod, {
			headers: {
				authorizationToken,
			},
			responseType: 'json',
		});

		return deliveryMethodData?.data?.deliveryMethod;
	}, []);

	useEffect(() => {
		setLoading(subscriptionLoading || defaultDeliveryMethodLoading || deliveryMethodLoading);
	}, [subscriptionLoading, defaultDeliveryMethodLoading, deliveryMethodLoading]);

	useDeepCompareEffect(() => {
		const currentDeliveryMethod = head(
			defaultDeliveryMethodData?.defaultMedicationDeliveryMethodForMember?.edges ?? []
		)?.node;

		if (doesNotExist(subscriptionData?.currentMember) || doesNotExist(currentDeliveryMethod)) {
			return;
		}

		const currentMember = subscriptionData?.currentMember;
		const currentSubscription: ISubscriptionResponse | undefined = find<
			ISubscriptionResponse,
			ISubscriptionResponse | undefined
		>(propEq('subscriptionType', currentDeliveryMethod?.subscriptionType))(
			currentMember?.memberSubscriptions?.nodes
		);
	}, [subscriptionData, defaultDeliveryMethodData]);

	const refinedContent: IMedicationDeliveryPostSubmitStepPageContent = keysToCamel(
		pageContent
	) as IMedicationDeliveryPostSubmitStepPageContent;

	const skeletonContent = (
		<FlexColumn gap="md">
			<Skeleton structure="column" repeated={4} fullWidth height="sm" gap="xs" />
			<Skeleton structure="column" repeated={3} fullWidth height="sm" gap="xs" />
			<SkeletonLink height="sm" />
		</FlexColumn>
	);

	const skeleton = (
		<FullWidthColumn gap="lgxl">
			<Skeleton height="lg" fullWidth />
			{skeletonContent}
			<Skeleton fullWidth height="xl" borderRadius="xl" />
		</FullWidthColumn>
	);

	return (
		<LoadingWrapper loading={loading} skeleton={skeleton}>
			<MedicationDeliveryPostSubmitStep
				pageContent={refinedContent}
				deliveryMethod={deliveryMethod}
				path={path}
			/>
		</LoadingWrapper>
	);
};

export default MedicationDeliveryPostSubmitStepPage;
